import classNames from 'classnames';
import type { TableDataValue } from 'components/DataTableTooltip';
import DataTableTooltip from 'components/DataTableTooltip';
import DurationFormat from 'components/DurationFormat';
import HourTags from 'components/HourTags';
import { BriefcaseOutline, Task, UserOutline } from 'components/Icons';
import RichTooltip from 'components/RichTooltip';
import { HOUR_MINUTES_POSTFIXED_DATE_FORMAT } from 'constants/datetime';
import type { HourTotals } from 'factories/calendar';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getDerivedDatesInfo, getFilter } from 'redux/selectors/calendar.filter';
import { getForecastEntities } from 'routes/forecast/redux/modules/forecast.selectors';
import type { TagBase } from 'types/tag';
import { isBetweenDates } from 'utils/date-helper';
import { fromISO } from 'utils/luxon-helpers';
import { truncateText } from 'utils/text-helper';
import styles from './HourSummaryTooltip.module.scss';

interface Props {
  suggestedTimeNotice?: string;
  projectId: number;
  projectName: string;
  clientId: number;
  clientName: string;
  tags?: Array<TagBase>;
  hourTotals?: HourTotals;
  hourDay: string;
  forecastId?: number | null;
  children?: React.ReactNode;
}

const HourSummaryTooltip: React.FC<Props> = ({
  suggestedTimeNotice,
  projectId,
  projectName,
  clientId,
  clientName,
  tags,
  hourTotals,
  forecastId,
  ...props
}) => {
  const projectTotals = hourTotals?.totalsByProject?.[projectId];
  const clientTotals = hourTotals?.totalsByClient?.[clientId];
  const { span, date } = useSelector(getFilter);
  const { since, upto } = useSelector(getDerivedDatesInfo);
  const forecastEntities = useSelector(getForecastEntities);

  // Find task from forecast
  const task = useMemo(() => {
    if (forecastId && forecastEntities) {
      const forecast = forecastEntities[forecastId];
      if (forecast) {
        return { title: forecast.title, type: 'forecast' as const };
      }
    }

    return null;
  }, [forecastId, forecastEntities]);

  const dateRange = useMemo(() => {
    return {
      from: span === 'month' ? fromISO(date).startOf('month').toISODate() : since,
      to: span === 'month' ? fromISO(date).endOf('month').toISODate() : upto,
    };
  }, [date, since, span, upto]);

  const handleDurationFormat = useCallback(
    (duration: number) => (
      <DurationFormat
        value={duration}
        valueType="seconds"
        format={HOUR_MINUTES_POSTFIXED_DATE_FORMAT}
        trim="both"
        zeroPlaceholder="—"
      />
    ),
    []
  );

  const isInRange =
    props.hourDay && dateRange.from && dateRange.to
      ? isBetweenDates(props.hourDay, dateRange.from, dateRange.to)
      : false;
  const amountToTruncate = 25;

  // Build data values for tooltip - task first if present, then client and project
  const dataValues = useMemo(() => {
    const values: Array<TableDataValue> = [];

    // Add task as header row if present (replaces "Logged time" title)
    if (task) {
      values.push({
        header: true,
        label: (
          <div className={styles.label}>
            <span className={styles.icon}>
              <Task width={12} height={12} fill="currentColor" />
            </span>
            <span>{task.title}</span>
          </div>
        ),
      });
    }

    // Always add divider after title (either task title or "Logged time")
    values.push('divider');

    // Add client
    values.push({
      label: (
        <div className={styles.label}>
          <span className={styles.icon}>
            <UserOutline />
          </span>
          <span>{truncateText(clientName, amountToTruncate)}</span>
        </div>
      ),
      value: handleDurationFormat(clientTotals?.loggedTotal || 0),
    });

    // Add project
    values.push({
      label: (
        <div className={styles.label}>
          <span className={styles.icon}>
            <BriefcaseOutline />
          </span>
          <span>{truncateText(projectName, amountToTruncate)}</span>
        </div>
      ),
      value: handleDurationFormat(projectTotals?.loggedTotal || 0),
    });

    return values;
  }, [
    task,
    clientName,
    projectName,
    clientTotals,
    projectTotals,
    handleDurationFormat,
    amountToTruncate,
  ]);

  if (!projectTotals || !clientTotals || !isInRange)
    return (
      <RichTooltip
        position="top-end"
        tooltip={
          <div className={classNames(styles.tooltip, styles.outOfRangeTooltip)}>
            {task && (
              <div className={styles.label}>
                <span className={styles.icon}>
                  <Task width={12} height={12} fill="currentColor" />
                </span>
                <span>{truncateText(task.title, amountToTruncate)}</span>
              </div>
            )}
            <div className={styles.label}>
              <span className={styles.icon}>
                <UserOutline />
              </span>
              <span>{truncateText(clientName, amountToTruncate)}</span>
            </div>
            <div className={styles.label}>
              <span className={styles.icon}>
                <BriefcaseOutline />
              </span>
              <span>{truncateText(projectName, amountToTruncate)}</span>
            </div>
            {!!tags?.length && (
              <HourTags className={styles.tagList} tags={tags} hasDarkBackground />
            )}
            {suggestedTimeNotice && <p className={styles.suggested}>{suggestedTimeNotice}</p>}
          </div>
        }
      >
        {props.children}
      </RichTooltip>
    );
  const dateFormat = { ...DateTime.DATE_MED, year: undefined };

  // When task is present, title is shown in dataValues as header - don't show "Logged time" title
  const tooltipTitle = task
    ? undefined
    : `Logged time, ${fromISO(dateRange.from).toLocaleString(
        dateFormat
      )} - ${fromISO(dateRange.to).toLocaleString(dateFormat)}`;

  return (
    <DataTableTooltip
      position="top-end"
      labelsClassName={styles.tooltip}
      title={tooltipTitle}
      dataValues={dataValues}
      footer={
        <div>
          {!!tags?.length && <HourTags className={styles.tagList} tags={tags} hasDarkBackground />}
          {suggestedTimeNotice && <p className={styles.suggested}>{suggestedTimeNotice}</p>}
        </div>
      }
    >
      {props.children}
    </DataTableTooltip>
  );
};

export default HourSummaryTooltip;
